import { useEffect, useRef } from "react";
import type { PosterFeatureProperties } from "../../api/posters";
import "./PosterContextMenu.css";

export interface PosterContextMenuProps {
    poster: PosterFeatureProperties;
    position: { x: number; y: number };
    onClose: () => void;
    onUndo?: () => void;
    onTakeDown?: () => void;
    onVandalized?: () => void;
    isLoading?: boolean;
}

const STATUS_LABELS: Record<number, string> = { 0: "Aktiv", 1: "Plockat ned", 2: "Vandaliserad" };
const STATUS_COLORS: Record<number, string> = { 0: "#E87716", 1: "#64748b", 2: "#D92626" };

const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
};

export const PosterContextMenu = ({
    poster,
    position,
    onClose,
    onUndo,
    onTakeDown,
    onVandalized,
    isLoading = false
}: PosterContextMenuProps): JSX.Element => {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [onClose]);

    const statusColor = STATUS_COLORS[poster.status] ?? "#E87716";
    const statusLabel = STATUS_LABELS[poster.status] ?? "Okänd";

    return (
        <div
            ref={menuRef}
            className="poster-context-menu"
            style={{ left: position.x, top: position.y }}
            role="menu"
        >
            <div className="poster-context-menu__header">
                <span className="poster-context-menu__dot" style={{ background: statusColor }} />
                <span className="poster-context-menu__title">
                    Affisch · {poster.quantity} st
                </span>
                <button
                    type="button"
                    className="poster-context-menu__close"
                    onClick={onClose}
                    aria-label="Stäng"
                >
                    ✕
                </button>
            </div>

            <div className="poster-context-menu__meta">
                {statusLabel} · {formatTime(poster.placedAt)}
            </div>

            <div className="poster-context-menu__actions">
                {poster.placedByMe && poster.status === 0 && onUndo && (
                    <button
                        type="button"
                        className="poster-context-menu__action poster-context-menu__action--undo"
                        onClick={() => { onUndo(); onClose(); }}
                        disabled={isLoading}
                        role="menuitem"
                    >
                        Ångra
                    </button>
                )}
                {poster.status === 0 && onTakeDown && (
                    <button
                        type="button"
                        className="poster-context-menu__action"
                        onClick={() => { onTakeDown(); onClose(); }}
                        disabled={isLoading}
                        role="menuitem"
                    >
                        Plocka ned
                    </button>
                )}
                {poster.status !== 2 && onVandalized && (
                    <button
                        type="button"
                        className="poster-context-menu__action poster-context-menu__action--danger"
                        onClick={() => { onVandalized(); onClose(); }}
                        disabled={isLoading}
                        role="menuitem"
                    >
                        Vandaliserad
                    </button>
                )}
            </div>
        </div>
    );
};
